import React, { useEffect, useMemo, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  type CameraActivityMeasurementResponse,
  type CameraActivityMetric,
  type CameraActivityScope,
} from '../../services/cameraActivityMeasurementApi';
import { useCameraActivityMeasurement } from '../../hooks/useCameraActivityMeasurement';
import {
  CAMERA_MEASUREMENT_HELPER,
  CAMERA_MEASUREMENT_OBSERVED_ONLY,
  CAMERA_MEASUREMENT_OBSERVED_QUALIFIER,
  CAMERA_MEASUREMENT_POSITIONING,
  CAMERA_MEASUREMENT_PRIVACY,
  CAMERA_MEASUREMENT_REASON_COPY,
  CAMERA_MEASUREMENT_UNAVAILABLE,
  CAMERA_MEASUREMENT_WINDOW_PRESETS,
  MEASUREMENT_QUALITY_LABEL,
  OVERVIEW_PHYSICAL_POLICY_CLOCK_HELPER,
  SCAN_MEASUREMENT_BASIS_TITLE,
  SCAN_MEASUREMENT_CLAIM_BOUNDARY,
  SCAN_MEASUREMENT_SIGNED_DISCLAIMER,
  SCAN_MEASUREMENT_UNAVAILABLE,
  SIMULTANEOUS_PRESENCE_HELPER,
  SIMULTANEOUS_PRESENCE_LABEL,
  cameraMeasurementLimitedGapCopy,
  cameraMeasurementObservedExceedsUsable,
  cameraMeasurementQualityReasonCopy,
  cameraMeasurementStatusLabel,
  cameraMeasurementSiteTimeHint,
  cameraMeasurementWindowForPreset,
  cameraMeasurementWindowLabel,
  formatCameraMeasurementWindowBounds,
  formatCameraMeasurementCompleteness,
  formatCameraMeasurementDuration,
  formatCameraMeasurementShare,
  type CameraMeasurementWindowPreset,
} from '../../domain/cameraMeasurement/cameraMeasurementPresentation';
import { formatHumanScanWindow } from '../../domain/acceptance/scanVerifyNavigation';
import { resolveSiteTimeZone } from '../../domain/siteTime/siteTime';
import { useSiteTimeZone } from '../../hooks/useSiteTimeZone';
import {
  CAMERA_MEASUREMENT_DURATION_GRAPH_KIND,
  CAMERA_MEASUREMENT_DURATION_OVERLAP_HELPER,
  cameraMeasurementAxisTicks,
  cameraMeasurementBarPercent,
  cameraMeasurementDurationChartTitle,
  cameraMeasurementShowsBar,
  cameraMeasurementWindowSeconds,
} from '../../domain/cameraMeasurement/cameraMeasurementDurationGraph';

const colors = {
  ink: '#0f172a',
  ink2: '#4a5768',
  ink3: '#7d8798',
  line: '#e3e8ef',
  blue: '#1d4ed8',
  blueSoft: '#eef3ff',
  greySoft: '#f8fafc',
};

export type CameraMeasurementPanelProps = {
  siteId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  inventoryDeviceId?: string | null;
  zoneId?: string | null;
  zoneName?: string | null;
  zoneVersion?: number | null;
  presentation?: 'compact' | 'contextual' | 'scan-context';
  windowStart?: string | null;
  windowEnd?: string | null;
  windowLabel?: string | null;
  signed?: boolean;
  override?: CameraActivityMeasurementResponse | null;
  now?: Date;
  defaultPreset?: CameraMeasurementWindowPreset;
  defaultScope?: CameraActivityScope;
  enabled?: boolean;
  defaultExpanded?: boolean;
  timeZone?: string | null;
};

const BAR_COLORS = {
  pedestrian: '#2563eb',
  forklift: '#7c3aed',
  simultaneous: '#0f766e',
};

function metricTestId(kind: 'pedestrian' | 'forklift' | 'simultaneous'): string {
  return `camera-measurement-${kind}`;
}

function DurationBarRow({
  kind,
  label,
  helper,
  metric,
  windowSeconds,
}: {
  kind: 'pedestrian' | 'forklift' | 'simultaneous';
  label: string;
  helper?: string;
  metric: CameraActivityMetric;
  windowSeconds: number;
}) {
  const measured = metric.state === 'MEASURED';
  const limited = metric.state === 'LIMITED';
  const unsupported = metric.state === 'UNSUPPORTED';
  const duration = formatCameraMeasurementDuration(metric.seconds);
  const share = formatCameraMeasurementShare(metric.share);
  const hasObserved = metric.seconds != null;
  const showBar = cameraMeasurementShowsBar(metric);
  const bar = showBar ? cameraMeasurementBarPercent(metric.seconds, windowSeconds) : null;
  const primary = measured || (limited && hasObserved)
    ? duration
    : unsupported
      ? 'Not supported'
      : CAMERA_MEASUREMENT_UNAVAILABLE;
  const secondary = measured
    ? `${share} of usable camera time`
    : limited && hasObserved
      ? CAMERA_MEASUREMENT_OBSERVED_QUALIFIER
      : cameraMeasurementStatusLabel(metric.state);
  return (
    <Box
      data-testid={metricTestId(kind)}
      data-metric-state={metric.state}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(132px, 28%) minmax(72px, 1fr) auto' },
        columnGap: '8px',
        rowGap: '2px',
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <Box sx={{ fontSize: 9.5, fontWeight: 700, color: colors.ink2, letterSpacing: '.2px' }}>{label}</Box>
      {showBar ? (
        <Box
          data-testid={`${metricTestId(kind)}-track`}
          sx={{
            height: 8,
            borderRadius: 999,
            bgcolor: '#e8eef6',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Box
            data-testid={`${metricTestId(kind)}-bar`}
            data-bar-percent={bar ? String(Math.round(bar.percent * 10) / 10) : '0'}
            data-overflow={bar?.overflow ? 'true' : 'false'}
            role="img"
            aria-label={`${label} ${primary}`}
            sx={{
              width: `${bar?.percent || 0}%`,
              height: '100%',
              bgcolor: BAR_COLORS[kind],
              borderRadius: 999,
            }}
          />
        </Box>
      ) : (
        <Box data-testid={`${metricTestId(kind)}-track`} sx={{ display: 'none' }} />
      )}
      <Box sx={{ justifySelf: { xs: 'start', sm: 'end' }, textAlign: { xs: 'left', sm: 'right' } }}>
        <Box
          data-testid={`${metricTestId(kind)}-value`}
          sx={{ fontSize: 12, fontWeight: 750, color: colors.ink, lineHeight: 1.2, whiteSpace: 'nowrap' }}
        >
          {primary}
        </Box>
        <Box
          data-testid={`${metricTestId(kind)}-share`}
          sx={{ fontSize: 9, color: colors.ink3, mt: '1px' }}
        >
          {secondary}
        </Box>
      </Box>
      {helper ? (
        <Box component="p" sx={{ m: '2px 0 0', fontSize: 9, color: colors.ink3, lineHeight: 1.35, gridColumn: { sm: '1 / -1' } }}>
          {helper}
        </Box>
      ) : null}
    </Box>
  );
}

function Chip({
  selected,
  children,
  onClick,
  testId,
}: {
  selected: boolean;
  children: React.ReactNode;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      data-testid={testId}
      aria-pressed={selected}
      onClick={onClick}
      sx={{
        fontSize: 9,
        fontWeight: 750,
        border: `1px solid ${selected ? colors.blue : colors.line}`,
        bgcolor: selected ? colors.blueSoft : '#fff',
        color: selected ? colors.blue : colors.ink2,
        borderRadius: '4px',
        px: '6px',
        py: '2px',
        cursor: 'pointer',
      }}
    >
      {children}
    </Box>
  );
}

function httpErrorCopy(status: number): string {
  if (status === 404) return 'This camera is not available.';
  if (status === 401 || status === 403) return 'Camera measurement is not available for this account.';
  if (status === 400) return 'The selected measurement request is not valid.';
  return 'Camera measurement is temporarily unavailable.';
}

export function CameraMeasurementPanel({
  siteId,
  deviceId,
  cameraId,
  inventoryDeviceId,
  zoneId,
  zoneName,
  zoneVersion,
  presentation = 'compact',
  windowStart = null,
  windowEnd = null,
  windowLabel: windowLabelOverride = null,
  signed = false,
  override = null,
  now,
  defaultPreset = 'last_complete_hour',
  defaultScope,
  enabled = true,
  defaultExpanded,
  timeZone = null,
}: CameraMeasurementPanelProps) {
  const loadedTimeZone = useSiteTimeZone(resolveSiteTimeZone(timeZone) ? null : siteId);
  const resolvedTimeZone = resolveSiteTimeZone(timeZone) || loadedTimeZone;
  const hasZone = Boolean(String(zoneId || '').trim());
  const scanContext = presentation === 'scan-context';
  const exactWindow = Boolean(String(windowStart || '').trim() && String(windowEnd || '').trim());
  const [preset, setPreset] = useState<CameraMeasurementWindowPreset>(defaultPreset);
  const [expanded, setExpanded] = useState(defaultExpanded ?? !scanContext);
  const [scope, setScope] = useState<CameraActivityScope>(
    defaultScope || (hasZone ? 'ZONE' : 'CAMERA'),
  );
  const [clock, setClock] = useState(() => now || new Date());
  useEffect(() => {
    if (now) {
      setClock(now);
      return undefined;
    }
    if (override || exactWindow) return undefined;
    const tick = window.setInterval(() => setClock(new Date()), 15000);
    return () => window.clearInterval(tick);
  }, [now, override, exactWindow]);
  const measurementNow = now || clock;
  const load = useCameraActivityMeasurement({
    siteId,
    deviceId,
    cameraId,
    inventoryDeviceId,
    scope,
    zoneId,
    zoneVersion,
    preset,
    windowStart: exactWindow ? windowStart : null,
    windowEnd: exactWindow ? windowEnd : null,
    now: measurementNow,
    override,
    enabled: enabled && expanded,
  });
  const selectedWindow = exactWindow
    ? { start: String(windowStart).trim(), end: String(windowEnd).trim() }
    : load.status === 'ok'
      ? load.data.window
      : cameraMeasurementWindowForPreset(preset, measurementNow);

  const scopeLabel = scope === 'ZONE'
    ? (zoneName ? `Zone: ${zoneName}` : 'Selected zone')
    : 'Entire camera view';
  const windowLabel = scanContext && exactWindow
    ? (String(windowLabelOverride || '').trim() || formatHumanScanWindow(windowStart, windowEnd, resolvedTimeZone))
    : cameraMeasurementWindowLabel(preset);
  const compact = presentation === 'compact';
  const title = scanContext ? SCAN_MEASUREMENT_BASIS_TITLE : 'Camera Measurement';
  const windowSeconds = cameraMeasurementWindowSeconds({
    start: selectedWindow.start,
    end: selectedWindow.end,
    preset,
  });
  const durationTitle = cameraMeasurementDurationChartTitle(preset);
  const axisTicks = cameraMeasurementAxisTicks(windowSeconds);

  const health = useMemo(() => {
    if (load.status !== 'ok') return null;
    const quality = load.data.quality;
    const observedExceedsUsable = cameraMeasurementObservedExceedsUsable(
      quality.source_observed_seconds,
      quality.usable_camera_seconds,
    );
    const ouConflict = observedExceedsUsable && (
      (load.data.reason_codes || []).includes('ACTIVITY_DATA_CONFLICT')
      || (quality.producer_completeness != null && quality.producer_completeness > 1)
    );
    const reason = cameraMeasurementQualityReasonCopy(load.data.reason_codes || [], quality);
    const completenessSource = (quality.producer_completeness != null && quality.producer_completeness > 1)
      ? null
      : quality.producer_completeness;
    const completeness = formatCameraMeasurementCompleteness(completenessSource);
    return {
      status: cameraMeasurementStatusLabel(load.data.state),
      state: load.data.state,
      usable: formatCameraMeasurementDuration(quality.usable_camera_seconds),
      observed: formatCameraMeasurementDuration(quality.source_observed_seconds),
      completeness,
      completenessIsAvailable: completenessSource != null,
      observedExceedsUsable,
      ouConflict,
      showObservedOfUsable: quality.source_observed_seconds != null && !observedExceedsUsable,
      reason,
      reasonCodes: load.data.reason_codes || [],
      producerGap: quality.producer_gap_seconds,
      gapCopy: quality.source_observed_seconds != null && !observedExceedsUsable
        ? cameraMeasurementLimitedGapCopy(quality.producer_gap_seconds)
        : null,
      method: load.data.provenance.measurement_definition_version,
      sourceCompatible: quality.source_compatible,
    };
  }, [load]);

  const identityUnavailable = load.status === 'identity_unavailable';
  const identityReason = identityUnavailable && load.reason === 'device_unresolved'
    ? CAMERA_MEASUREMENT_REASON_COPY.device_scope_unresolved
    : identityUnavailable
      ? 'Camera measurement needs a site, device, and camera.'
      : null;

  const body = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          {scanContext ? null : (
            <>
              <Box component="h3" sx={{ m: 0, fontSize: 11, fontWeight: 750, color: colors.ink }}>
                {title}
              </Box>
              <Box sx={{ fontSize: 9.5, color: colors.ink2, mt: '2px' }}>{CAMERA_MEASUREMENT_POSITIONING}</Box>
            </>
          )}
          <Box
            data-testid="camera-measurement-scope"
            data-scope={scope}
            sx={{ fontSize: 9.5, color: colors.ink, mt: scanContext ? 0 : '3px', fontWeight: 650 }}
          >
            {scopeLabel}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
          <Box
            data-testid="camera-measurement-window"
            data-window-start={selectedWindow.start}
            data-window-end={selectedWindow.end}
            sx={{ fontSize: 9.5, fontWeight: 700, color: colors.ink, textAlign: 'right' }}
          >
            {windowLabel}
            {scanContext ? null : (
              <Box
                data-testid="camera-measurement-window-bounds"
                sx={{ mt: '2px', fontSize: 9, fontWeight: 650, color: colors.ink2 }}
              >
                {formatCameraMeasurementWindowBounds(
                  selectedWindow.start,
                  selectedWindow.end,
                  resolvedTimeZone,
                )}
                {cameraMeasurementSiteTimeHint(resolvedTimeZone) ? (
                  <Box component="span" sx={{ display: 'block', fontWeight: 500, color: colors.ink3 }}>
                    {cameraMeasurementSiteTimeHint(resolvedTimeZone)}
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
          {scanContext ? null : (
            <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {CAMERA_MEASUREMENT_WINDOW_PRESETS.map((choice) => (
                <Chip
                  key={choice.id}
                  selected={preset === choice.id}
                  testId={`camera-measurement-window-${choice.id}`}
                  onClick={() => setPreset(choice.id)}
                >
                  {choice.label.replace('Last complete ', '')}
                </Chip>
              ))}
            </Box>
          )}
          {hasZone && !scanContext ? (
            <Box sx={{ display: 'flex', gap: '4px' }}>
              <Chip
                selected={scope === 'CAMERA'}
                testId="camera-measurement-scope-camera"
                onClick={() => setScope('CAMERA')}
              >
                Entire camera view
              </Chip>
              <Chip
                selected={scope === 'ZONE'}
                testId="camera-measurement-scope-zone"
                onClick={() => setScope('ZONE')}
              >
                {zoneName || 'Zone'}
              </Chip>
            </Box>
          ) : null}
        </Box>
      </Box>

      {scanContext ? (
        <Box data-testid="scan-measurement-claim-boundary" sx={{ fontSize: 9.5, color: colors.ink2, mt: '6px', lineHeight: 1.4 }}>
          {SCAN_MEASUREMENT_CLAIM_BOUNDARY}
        </Box>
      ) : null}
      {scanContext && signed ? (
        <Box data-testid="scan-measurement-signed-disclaimer" sx={{ fontSize: 9.5, color: colors.ink2, mt: '4px', lineHeight: 1.4 }}>
          {SCAN_MEASUREMENT_SIGNED_DISCLAIMER}
        </Box>
      ) : null}

      {load.status === 'idle' && scanContext ? (
        <Box data-testid="camera-measurement-idle" role="status" sx={{ fontSize: 11, color: colors.ink2, mt: '8px' }}>
          Expand to load camera measurement for this Scan window.
        </Box>
      ) : null}

      {load.status === 'loading' ? (
        <Box
          data-testid="camera-measurement-loading"
          role="status"
          sx={{ fontSize: 11, color: colors.ink2, mt: '8px' }}
        >
          Loading camera measurement…
        </Box>
      ) : null}

      {load.status === 'http' ? (
        <Box
          data-testid="camera-measurement-error"
          data-http-status={load.httpStatus}
          data-error-kind={load.httpStatus === 404 ? 'resource' : 'request'}
          role="status"
          sx={{ fontSize: 11, color: colors.ink2, mt: '8px' }}
        >
          {httpErrorCopy(load.httpStatus)}
        </Box>
      ) : null}

      {load.status === 'network' ? (
        <Box data-testid="camera-measurement-error" data-error-kind="network" role="status" sx={{ fontSize: 11, color: colors.ink2, mt: '8px' }}>
          Camera measurement is temporarily unavailable.
        </Box>
      ) : null}

      {identityUnavailable ? (
        <Box
          data-testid="camera-measurement-identity-unavailable"
          data-identity-reason={load.reason}
          role="status"
          sx={{ fontSize: 11, color: colors.ink2, mt: '8px' }}
        >
          {scanContext ? (
            <Box>{SCAN_MEASUREMENT_UNAVAILABLE}</Box>
          ) : (
            <>
              <Box data-testid="camera-measurement-status" data-state="LIMITED" sx={{ fontWeight: 700, color: colors.ink }}>
                Limited
              </Box>
              <Box data-testid="camera-measurement-reason" sx={{ mt: '2px' }}>{identityReason}</Box>
            </>
          )}
        </Box>
      ) : null}

      {load.status === 'ok' ? (
        <>
          <Box
            data-testid="camera-measurement-duration-graph"
            data-chart-kind={CAMERA_MEASUREMENT_DURATION_GRAPH_KIND}
            data-axis-max={String(windowSeconds)}
            data-window-preset={preset}
            sx={{ mt: '8px', minWidth: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '6px' }}>
              <Box
                component="h4"
                data-testid="camera-measurement-duration-title"
                sx={{ m: 0, fontSize: 9.5, fontWeight: 750, color: colors.ink2 }}
              >
                {durationTitle}
              </Box>
              <Tooltip title={CAMERA_MEASUREMENT_DURATION_OVERLAP_HELPER}>
                <Box
                  component="span"
                  data-testid="camera-measurement-duration-help"
                  tabIndex={0}
                  aria-label={CAMERA_MEASUREMENT_DURATION_OVERLAP_HELPER}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    border: `1px solid ${colors.ink3}`,
                    color: colors.ink3,
                    fontSize: 8,
                    fontWeight: 800,
                    display: 'inline-grid',
                    placeItems: 'center',
                    cursor: 'help',
                    lineHeight: 1,
                  }}
                >
                  i
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <DurationBarRow
                kind="pedestrian"
                label="Pedestrian presence"
                metric={load.data.metrics.pedestrian_presence}
                windowSeconds={windowSeconds}
              />
              <DurationBarRow
                kind="forklift"
                label="Forklift presence"
                metric={load.data.metrics.forklift_presence}
                windowSeconds={windowSeconds}
              />
              <DurationBarRow
                kind="simultaneous"
                label={SIMULTANEOUS_PRESENCE_LABEL}
                helper={compact ? undefined : SIMULTANEOUS_PRESENCE_HELPER}
                metric={load.data.metrics.simultaneous_presence}
                windowSeconds={windowSeconds}
              />
            </Box>
            {axisTicks.length && (
              cameraMeasurementShowsBar(load.data.metrics.pedestrian_presence)
              || cameraMeasurementShowsBar(load.data.metrics.forklift_presence)
              || cameraMeasurementShowsBar(load.data.metrics.simultaneous_presence)
            ) ? (
              <Box
                data-testid="camera-measurement-duration-axis"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: '5px',
                  fontSize: 8.5,
                  color: colors.ink3,
                }}
              >
                {axisTicks.map((tick) => (
                  <Box key={`${tick.seconds}-${tick.label}`}>{tick.label}</Box>
                ))}
              </Box>
            ) : null}
          </Box>
          <Box
            data-testid="camera-measurement-health"
            data-quality="true"
            sx={{ mt: '8px', fontSize: 9.5, color: colors.ink2, lineHeight: 1.4 }}
          >
            <Box
              component="span"
              data-testid="camera-measurement-quality"
              sx={{ fontWeight: 700, color: colors.ink3, letterSpacing: '.4px', textTransform: 'uppercase', fontSize: 8.5 }}
            >
              {MEASUREMENT_QUALITY_LABEL}
            </Box>
            <Box sx={{ mt: '2px' }}>
              <Box component="span" data-testid="camera-measurement-status" data-state={load.data.state} sx={{ fontWeight: 700, color: colors.ink }}>
                {health?.status}
              </Box>
              {health?.state === 'LIMITED' && !health.ouConflict ? (
                <>
                  {health.completenessIsAvailable ? (
                    <>
                      {' · '}
                      <Box component="b" data-testid="camera-measurement-completeness">
                        {health.completeness} complete
                      </Box>
                    </>
                  ) : (
                    <>
                      {' · '}
                      Completeness{' '}
                      <Box component="b" data-testid="camera-measurement-completeness">
                        {health.completeness}
                      </Box>
                    </>
                  )}
                </>
              ) : health?.state === 'LIMITED' ? (
                <Box component="b" data-testid="camera-measurement-completeness" sx={{ display: 'none' }}>
                  {health.completeness}
                </Box>
              ) : (
                <>
                  {' · '}
                  Usable camera time{' '}
                  <Box component="b" data-testid="camera-measurement-usable">
                    {health?.usable}
                  </Box>
                  {' · '}
                  Completeness{' '}
                  <Box component="b" data-testid="camera-measurement-completeness">
                    {health?.completeness}
                  </Box>
                </>
              )}
            </Box>
            {health?.state === 'LIMITED' && health.showObservedOfUsable ? (
              <Box sx={{ mt: '2px' }} data-testid="camera-measurement-observed-basis">
                <Box component="b" data-testid="camera-measurement-observed">
                  {health.observed}
                </Box>
                {' observed of '}
                <Box component="b" data-testid="camera-measurement-usable">
                  {health.usable}
                </Box>
                {' usable camera time'}
              </Box>
            ) : health?.state === 'LIMITED' && health.ouConflict ? (
              <Box data-testid="camera-measurement-observed-basis" sx={{ display: 'none' }} />
            ) : health?.state === 'LIMITED' ? (
              <Box sx={{ mt: '2px' }} data-testid="camera-measurement-observed-basis">
                Usable camera time{' '}
                <Box component="b" data-testid="camera-measurement-usable">
                  {health.usable}
                </Box>
                {' · Producer observed '}
                <Box component="b" data-testid="camera-measurement-observed">
                  {health.observed}
                </Box>
              </Box>
            ) : null}
            {health?.state === 'LIMITED' && health.gapCopy && (health.reasonCodes || []).includes('READING_SOURCE_GAP') ? (
              <Box data-testid="camera-measurement-reason" data-reason-codes={(health.reasonCodes || []).join(',')} sx={{ mt: '2px' }}>
                {health.gapCopy}
                {' '}
                {CAMERA_MEASUREMENT_OBSERVED_ONLY}
              </Box>
            ) : health?.reason ? (
              <Box data-testid="camera-measurement-reason" data-reason-codes={(health.reasonCodes || []).join(',')} sx={{ mt: '2px' }}>
                {health.reason}
              </Box>
            ) : (
              <Box data-testid="camera-measurement-reason" data-reason-codes="" sx={{ display: 'none' }} />
            )}
            {scanContext && load.status === 'ok' ? (
              <Box component="details" data-testid="camera-measurement-quality-disclosure" sx={{ mt: '4px' }}>
                <Box component="summary" sx={{ cursor: 'pointer', fontSize: 9, color: colors.blue, fontWeight: 700 }}>
                  Measurement method
                </Box>
                <Box sx={{ mt: '2px', fontSize: 9, color: colors.ink3 }}>
                  {health?.method ? <Box>Method {health.method}</Box> : null}
                  {health?.sourceCompatible === false ? <Box>Source quality: not compatible across this window</Box> : null}
                  {health?.producerGap != null ? (
                    <Box data-testid="camera-measurement-producer-gap">
                      Producer gap {formatCameraMeasurementDuration(health.producerGap)}
                    </Box>
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}

      {(!scanContext || expanded) ? (
        <Box data-testid="camera-measurement-privacy" sx={{ fontSize: 9, color: colors.ink3, mt: '6px' }}>
          {CAMERA_MEASUREMENT_PRIVACY}
        </Box>
      ) : null}
      {compact ? (
        <Box data-testid="overview-physical-policy-clock-helper" sx={{ fontSize: 9, color: colors.ink3, mt: '3px' }}>
          {OVERVIEW_PHYSICAL_POLICY_CLOCK_HELPER}
        </Box>
      ) : (
        <Box sx={{ fontSize: 9, color: colors.ink3, mt: '3px' }}>{CAMERA_MEASUREMENT_HELPER}</Box>
      )}
    </>
  );

  if (scanContext) {
    return (
      <Box
        component="section"
        data-testid="scan-measurement-basis"
        data-presentation={presentation}
        data-expanded={expanded ? 'true' : 'false'}
        aria-label={title}
        sx={{
          bgcolor: colors.greySoft,
          border: `1px solid ${colors.line}`,
          borderRadius: '8px',
          p: '10px 12px',
          minWidth: 0,
        }}
      >
        <Box
          component="button"
          type="button"
          data-testid="scan-measurement-basis-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            border: 0,
            bgcolor: 'transparent',
            p: 0,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <Box component="h3" sx={{ m: 0, fontSize: 11, fontWeight: 750, color: colors.ink }}>
            {title}
          </Box>
          <Box sx={{ fontSize: 9.5, fontWeight: 700, color: colors.blue }}>
            {expanded ? 'Hide' : 'Show'}
          </Box>
        </Box>
        {expanded ? <Box sx={{ mt: '8px' }}>{body}</Box> : (
          <Box sx={{ fontSize: 9.5, color: colors.ink3, mt: '4px' }}>
            Physical measurement context for this Scan window. Collapsed by default.
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      data-testid="camera-measurement-panel"
      data-presentation={presentation}
      aria-label="Camera Measurement"
      sx={{
        bgcolor: colors.greySoft,
        borderBottom: compact ? `1px solid ${colors.line}` : 0,
        p: compact ? '8px 12px 9px' : '12px',
        minWidth: 0,
      }}
    >
      {body}
    </Box>
  );
}

export default CameraMeasurementPanel;
